import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ReportingWebhook as UIAPIReportingWebhook,
  ReportingWebhooksWebhookCreateUpdateRequest,
  ReportingWebhooksWebhookRequest,
  VerifyReportingWebhookRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { removeWebhookCache, setWebhookCache } from './cache';
import { parseWebhook, type Webhook } from './entity';
import { normalizeEnabled } from './utils';

/**
 * Reporting-webhook mutations. The backend owns webhook validation, so saves
 * are not optimistic; hooks write the canonical response into the cache.
 *
 * The `enabled = false when no events` invariant is enforced here (via
 * `normalizeEnabled`) rather than in the form, so every writer honors it —
 * matching the Ember new/edit controllers.
 */

/**
 * The create/update body fields — the generated `webhook` request object, so
 * the write surface can't drift from the spec (snake_case, per the wire).
 */
export type WebhookWriteInput = ReportingWebhooksWebhookRequest;

function requireWebhook(res: { webhook?: UIAPIReportingWebhook }): UIAPIReportingWebhook {
  if (res.webhook == null) {
    throw new Error('reporting webhook response did not include a webhook');
  }
  return res.webhook;
}

function webhookRequestBody(input: WebhookWriteInput): ReportingWebhooksWebhookCreateUpdateRequest {
  return {
    webhook: {
      ...input,
      // Force-disable a webhook with no events, regardless of the passed flag.
      enabled: normalizeEnabled({ enabled: input.enabled, events: input.events }),
    },
  };
}

/* ------------------------------------------------------------------------- */
/* createWebhook                                                              */
/* ------------------------------------------------------------------------- */

export interface CreateWebhookInput extends WebhookWriteInput {
  workspaceId: number;
}

/** @public */
export async function createWebhook({ workspaceId, ...write }: CreateWebhookInput): Promise<Webhook> {
  const res = await hydraUiApi.reportingWebhooks.createWebhooks({
    params: { path: { environment_id: String(workspaceId) } },
    body: webhookRequestBody(write),
  });
  return parseWebhook(requireWebhook(res));
}

type UseCreateWebhookVariables = Omit<CreateWebhookInput, 'workspaceId'>;

interface UseCreateWebhookOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Webhook, UseCreateWebhookVariables>;
}

/** @public */
export const useCreateWebhook = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCreateWebhookOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateWebhookVariables) => createWebhook({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      setWebhookCache(qc, workspaceId, created);
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* updateWebhook                                                              */
/* ------------------------------------------------------------------------- */

export interface UpdateWebhookInput extends WebhookWriteInput {
  workspaceId: number;
  webhookId: string;
}

/** @public */
export async function updateWebhook({ workspaceId, webhookId, ...write }: UpdateWebhookInput): Promise<Webhook> {
  const res = await hydraUiApi.reportingWebhooks.updateWebhooks({
    params: { path: { environment_id: String(workspaceId), id: webhookId } },
    body: webhookRequestBody(write),
  });
  return parseWebhook(requireWebhook(res));
}

type UseUpdateWebhookVariables = Omit<UpdateWebhookInput, 'workspaceId'>;

interface UseUpdateWebhookOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Webhook, UseUpdateWebhookVariables>;
}

/** @public */
export const useUpdateWebhook = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseUpdateWebhookOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateWebhookVariables) => updateWebhook({ workspaceId, ...variables }),
    onSuccess: (updated, ...callbackArgs) => {
      setWebhookCache(qc, workspaceId, updated);
      onSuccess?.(updated, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* deleteWebhook                                                              */
/* ------------------------------------------------------------------------- */

export interface DeleteWebhookInput {
  workspaceId: number;
  webhookId: string;
}

/** @public */
export async function deleteWebhook(input: DeleteWebhookInput): Promise<void> {
  await hydraUiApi.reportingWebhooks.deleteWebhooks({
    params: { path: { environment_id: String(input.workspaceId), id: input.webhookId } },
  });
}

type UseDeleteWebhookVariables = Omit<DeleteWebhookInput, 'workspaceId'>;

interface UseDeleteWebhookOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteWebhookVariables>;
}

/** @public */
export const useDeleteWebhook = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteWebhookOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteWebhookVariables) => deleteWebhook({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      removeWebhookCache(qc, workspaceId, variables.webhookId);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* regenerateSigningKey                                                       */
/* ------------------------------------------------------------------------- */

export interface RegenerateSigningKeyInput {
  workspaceId: number;
  webhookId: string;
}

/** @public */
export async function regenerateSigningKey(input: RegenerateSigningKeyInput): Promise<Webhook> {
  const res = await hydraUiApi.reportingWebhooks.webhooksSigningKey({
    params: { path: { environment_id: String(input.workspaceId), id: input.webhookId } },
  });
  return parseWebhook(requireWebhook(res));
}

type UseRegenerateSigningKeyVariables = Omit<RegenerateSigningKeyInput, 'workspaceId'>;

interface UseRegenerateSigningKeyOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Webhook, UseRegenerateSigningKeyVariables>;
}

/** @public */
export const useRegenerateSigningKey = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseRegenerateSigningKeyOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseRegenerateSigningKeyVariables) => regenerateSigningKey({ workspaceId, ...variables }),
    onSuccess: (rotated, ...callbackArgs) => {
      // Patch the row in place — never invalidate, or the freshly-returned
      // signing key would disappear before the user can copy it.
      setWebhookCache(qc, workspaceId, rotated);
      onSuccess?.(rotated, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* verifyWebhook                                                              */
/* ------------------------------------------------------------------------- */

/**
 * The verify request body (`endpoint` / `signing_key` / `with_content`) taken
 * straight from the generated endpoint type, plus the workspace id — no
 * camelCase alias, so it stays in lockstep with the spec.
 */
export interface VerifyWebhookInput extends VerifyReportingWebhookRequest {
  workspaceId: number;
}

/**
 * Test a reporting-webhook endpoint. Resolves on a 2xx (no body) and rejects
 * on a non-2xx — the list row uses success/failure as the verify signal.
 *
 * @public
 */
export async function verifyWebhook({ workspaceId, ...body }: VerifyWebhookInput): Promise<void> {
  await hydraUiApi.verify.reportingWebhook({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

type UseVerifyWebhookVariables = Omit<VerifyWebhookInput, 'workspaceId'>;

interface UseVerifyWebhookOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseVerifyWebhookVariables>;
}

/** @public */
export const useVerifyWebhook = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseVerifyWebhookOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseVerifyWebhookVariables) => verifyWebhook({ workspaceId, ...variables }),
    onSuccess,
  });
};
